import type { CustomRuleSource } from '@/lib/custom-rules/sources';

import { YAML_CUSTOM_RULE_SOURCE } from '@/lib/custom-rules/sources';

/**
 * What manages a custom rule row:
 * - `repository`: a dashboard agent on this repository, editable row by row.
 * - `organization`: this repository's row of an organization agent. The agent manages it, so the
 *   row is read-only; edit the agent in the Agents library instead.
 * - `yaml`: a cache of a cubic.yaml rule, read-only in the dashboard.
 *
 * Organization rows keep `managedBy: 'ui'`, so check ownership with this helper rather than
 * comparing `managedBy`.
 */
export type CustomRuleOwner = 'repository' | 'organization' | 'yaml';

type RuleOwnershipFields = {
  managedBy: CustomRuleSource;
  organizationRuleId?: string | null;
};

export function getCustomRuleOwner(rule: RuleOwnershipFields): CustomRuleOwner {
  if (rule.managedBy === YAML_CUSTOM_RULE_SOURCE) {
    return 'yaml';
  }

  return rule.organizationRuleId ? 'organization' : 'repository';
}

/** True for dashboard agents that belong to one repository and can be edited row by row. */
export function isRepositoryAgentRule(rule: RuleOwnershipFields): boolean {
  return getCustomRuleOwner(rule) === 'repository';
}

/** True for a repository's row of an organization agent. */
export function isOrganizationAgentRule(rule: RuleOwnershipFields): boolean {
  return getCustomRuleOwner(rule) === 'organization';
}

/**
 * True for an enabled rule that holds one of its repository's agent slots when a new agent is
 * saved. Rows of organization agents don't: they run only in the slots the repository leaves free.
 */
export function countsTowardAgentLimit(rule: RuleOwnershipFields & { enabled: boolean }): boolean {
  return rule.enabled && !isOrganizationAgentRule(rule);
}
